import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import {
  faqs,
  howItWorks,
  InventoryCard,
  SocialInfoContent,
  TentInfoContent,
  inventoryCardSuiteModal,
  tentSetupForNumber,
  tentSizeForNumber,
  socialSuiteNames,
} from "./helpers";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";

import type { TeamConfig } from "../types";

const BannerImage =
  "https://ss-leagues.s3.amazonaws.com/NCAA/Iowa%20State/Gameday%20Hospitality%20Banner_1486x148.png";
const StaticSeatMapImage =
  "https://ss-leagues.s3.amazonaws.com/NCAA/Iowa%20State/iowa-state-seat-map.png";
const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/NCAA/iowa-state-university.png";
const BackgroundImage =
  "https://ss-leagues.s3.amazonaws.com/NCAA/Iowa%20State/Background%20Image%281920x1080px%29.jpg";

const MktgBase = "https://ss-leagues.s3.amazonaws.com/NCAA/Iowa%20State%20Mktg";
const ReserveTentsMap = `${MktgBase}/Gameday-Hospitality_Reserve-Tents-zoomed.png`;
const SocialGardenMap = `${MktgBase}/social%20garden.png`;

// per-tent carousel images, keyed by mapSectionName (3D node id minus "S_")
const tentImage = (n: number, size: string) => [
  `${MktgBase}/reserve%20tents.png`,
  `${MktgBase}/Tent%20${n}/${size}.png`,
  `${MktgBase}/Tent%20${n}/Gameday%20Hospitality_Reserve%20Tents_${n}.png`,
];

const SuiteImages = {
  TENT1: tentImage(1, "20x30"),
  TENT2: tentImage(2, "20x30"),
  TENT3: tentImage(3, "20x30"),
  TENT4: tentImage(4, "40x40"),
  TENT5: tentImage(5, "40x40"),
  TENT6: tentImage(6, "40x40"),
  TENT7: tentImage(7, "40x100"),
  TENT8: tentImage(8, "40x60"),
  TENT9: tentImage(9, "40x60"),
  TENT10: tentImage(10, "40x60"),
};

const openSuiteModal = (title: string, content: JSX.Element) =>
  inventoryCardSuiteModal.getState().set({ isOpen: true, title, content });

const config: TeamConfig = {
  teamSchema: "iowastate_tents",
  browserTitle: "Iowa State FLEX Tents",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Iowa State University",

  manageCard: true,

  // seat map configs
  seatMapConfigs: {
    venueId: "",
    mapId: "",
    staticSeatMapImage: StaticSeatMapImage,
    customSeatMapStaticImageStyle: {
      width: "75%",
      marginX: "auto",
      display: "block",
    },
    // per-event-type static map (depends on event `categories` being set in DB)
    dynamicSeatMapStaticImages: [
      { category: "Tents", staticSeatMapImage: ReserveTentsMap },
      { category: "Social", staticSeatMapImage: SocialGardenMap },
    ],
    // per-suite "View Suite" carousel (depends on `mapSectionName` set in DB)
    suiteImages: SuiteImages,
    // no 3D venue (empty venueId/mapId) — suppress the broken default 3D slide
    hide3dViewFromSlider: true,
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: false,
  inventoryType: inventoryTypeUIDs.suiteLevelV2,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    defaultSeatCount: 1,
    hideViewTicketsInThankYouPage: true,
    isPerksEnabled: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/isu/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  // images
  images: {
    logoNameImage: "",
    faviconImage: LogoImage,
    seatMap3dPreviewImage: "",
    findGamesBannerImage: BannerImage,
    authBackgroundImage: BackgroundImage,
    giftCardStandalonePageImage: BannerImage,
    checkoutStatusPageImage: BackgroundImage,
    logoImage: LogoImage + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },

    inventoryCardSettings: {
      hideFBPackageLineItem: true,
      hideSeatCount: true,
      seatCountLabel: "Ticket Count: ",
      viewSuiteButtonLabel: "View Tent",
      checkoutInventoryCard: {
        seatCountLabel: "Ticket Count: ",
      },
      suiteLevelInventoryCard: {
        customButtons: [
          ...Array.from({ length: 10 }, (_, i) => {
            const n = i + 1;
            return {
              id: n,
              label: "More Info",
              suiteNames: [`TENT${n}`],
              onClick: () =>
                openSuiteModal(
                  "More Info",
                  <TentInfoContent
                    title={`Reserve Tent ${n} (${tentSizeForNumber(n)})`}
                    setup={tentSetupForNumber(n)}
                  />,
                ),
            };
          }),
          {
            id: 11,
            label: "More Info",
            suiteNames: socialSuiteNames,
            onClick: () => openSuiteModal("More Info", <SocialInfoContent />),
          },
        ],
        renderBody: [{ id: 1, component: () => <InventoryCard /> }],
      },
    },
  },

  colors: {
    primary: "#C8102E",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: null,
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Thank you for your purchase with Iowa State University! You will be notified when your tickets are available.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },
    perksPage: {
      perkItemPriceLabel: "ticket",
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
